/**
 * Interventie photo section for the project Dashboard.
 *
 * The interventie (repair) field worker uploads photos from their own shell; before
 * the dashboard replaced the multi-tab ProjectView those photos were only reachable
 * through the old vordering wizard. This section restores a dedicated viewer on the
 * dashboard: interventie photos grouped by upload date (no kringen / m² / systeem — it
 * is "like a collector but without all that"), with upload + delete for PL/admin.
 *
 * The vordering flow selects interventie date-groups separately, inside
 * DashboardVorderingSection — this component is a pure viewer/uploader and never
 * touches billing. A mutation here bumps the parent's `onChanged` so the vordering
 * section reloads its selectable date-groups.
 *
 * Photos are served through the ADR-022 proxy (`p.url`), never presigned.
 *
 * One instance = one block. `DashboardInterventieBlocks` renders one per fase
 * carrying the Odoo `Interventie` tag (`faseId` + `select`: uploads go to that
 * fase's id-keyed folder, the list shows only its photos) and one "Oudere
 * interventiefoto's" block for everything else (`allowUpload={false}`). Without a
 * `faseId` the block is the old project-wide section, uploading to the pile.
 * The list is always fetched project-wide and filtered at render, so delete and
 * re-date keep using the project routes that accept every generation of key.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { PhotoItem } from '@/types/odoo';
import { usePermissionsStore } from '@/stores/permissions';
import { PhotoLightbox } from '@/components/ui/PhotoLightbox';
import { PhotoGridThumb } from '@/components/ui/PhotoGridThumb';
import { isPdfPhoto } from '@/utils/photoFiles';
import { compressImage } from '@/utils/imageCompression';
import { uploadPool } from '@/utils/uploadPool';
import { describeUploadFailures } from '@/utils/uploadFeedback';
import {
  fetchProjectInterventiePhotos,
  uploadProjectInterventiePhoto,
  uploadTaskPhoto,
  deleteProjectInterventiePhoto,
  redateProjectInterventiePhotos,
} from '@/api/interventie';
import { groupInterventieByDate } from './vordering/vorderingHelpers';
import { fotoCount } from '@/utils/interventiePhotos';

interface DashboardInterventieSectionProps {
  projectId: number;
  /** The project's planning days (yyyy-mm-dd + fase label), the only dates a group can
   *  be re-dated onto so interventie photos snap to real planned work days. */
  planningDates?: Array<{ date: string; label: string }>;
  /** Bumped on every photo mutation (upload/delete/re-date) so the Vordering section
   *  reloads its selectable interventie date-groups without a full page reload. */
  onChanged?: () => void;
  /** Rendered inside a DashboardSection coloured shell — drop our own card chrome. */
  embedded?: boolean;
  /** Upload into this fase's id-keyed folder instead of the project pile. */
  faseId?: number;
  /** Which of the project's interventie photos this block shows (default: all). */
  select?: (photos: PhotoItem[]) => PhotoItem[];
  /** Offer upload at all (the "Oudere" block does not). Still gated on photo_upload. */
  allowUpload?: boolean;
  /** Block heading, shown in the toolbar row (used when several blocks are stacked). */
  heading?: ReactNode;
  /** Render nothing once loaded with no photos (the "Oudere" block). */
  hideWhenEmpty?: boolean;
}

/** yyyy-mm-dd → dd-mm-yyyy (string slice — never toISOString, per the CET date gotcha). */
function fmtDay(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return d && m && y ? `${d}-${m}-${y}` : iso;
}

/** One rendered block: a date group (label `dd-mm-yyyy`) or the "Overige" catch-all. */
interface DisplayGroup {
  id: string;
  label: string;
  photos: PhotoItem[];
}

/**
 * Build the render groups from the flat photo list. Matched photos (filename starts
 * `yyyymmdd…`) fall into date groups via the shared `groupInterventieByDate` helper —
 * the SAME grouping the vordering flow bills on, so viewer and billing stay in sync.
 * Anything the helper drops (legacy n8n-era keys without a date prefix) lands in an
 * "Overige" bucket so no photo silently vanishes (CLAUDE.md: legacy-format bug class).
 * "Overige" photos are viewable here but not foldable into a vordering — the vordering
 * flow bills by date-group, which needs the yyyymmdd prefix.
 */
function buildDisplayGroups(photos: PhotoItem[]): DisplayGroup[] {
  const byKey = new Map(photos.map((p) => [p.key, p]));
  const dateGroups = groupInterventieByDate(photos);
  const matched = new Set<string>();
  const groups: DisplayGroup[] = dateGroups.map((g) => {
    const groupPhotos: PhotoItem[] = [];
    for (const key of g.keys) {
      matched.add(key);
      const p = byKey.get(key);
      if (p) groupPhotos.push(p);
    }
    return { id: g.date, label: g.label, photos: groupPhotos };
  });
  const leftover = photos.filter((p) => !matched.has(p.key));
  if (leftover.length > 0) {
    groups.push({ id: 'overige', label: 'Overige', photos: leftover });
  }
  return groups;
}

export function DashboardInterventieSection({ projectId, planningDates = [], onChanged, embedded = false, faseId, select, allowUpload = true, heading, hideWhenEmpty = false }: DashboardInterventieSectionProps) {
  const canFeature = usePermissionsStore((s) => s.canFeature);
  const canUpload = allowUpload && canFeature('photo_upload');
  const canDelete = canFeature('photo_approve');
  // Re-dating is a write (re-keys the photos) — gate on the same feature as upload,
  // but not on `allowUpload`: the "Oudere" block takes no new photos yet its legacy
  // photos must stay re-datable.
  const canRedate = canFeature('photo_upload');

  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  // Lightbox scoped to the clicked group's photos (keeps prev/next inside the group).
  const [lightbox, setLightbox] = useState<{ photos: PhotoItem[]; index: number } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  // Drop stale responses after a project switch (this component may not remount).
  const projectIdRef = useRef(projectId);

  const load = useCallback(() => {
    const pid = projectId;
    projectIdRef.current = pid;
    setLoading(true);
    setLoadError(null);
    fetchProjectInterventiePhotos(pid)
      .then((res) => {
        if (projectIdRef.current !== pid) return;
        setPhotos(res);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (projectIdRef.current !== pid) return;
        // Surface the error instead of the old sidebar's silent swallow-to-[] —
        // this is now the canonical "see all interventie photos" surface.
        setLoadError(err instanceof Error ? err.message : "Interventiefoto's laden mislukt");
        setLoading(false);
      });
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => buildDisplayGroups(select ? select(photos) : photos), [photos, select]);

  const handleUpload = async (files: FileList) => {
    // Unfiltered on purpose: an unsupported type is rejected by compressImage
    // with a reason the failure message shows, so a bad file in a mixed batch is
    // reported rather than silently skipped.
    const fileArr = Array.from(files);
    if (fileArr.length === 0) return;
    setUploading(true);
    setUploadError(null);
    setUploadProgress({ done: 0, total: fileArr.length });

    const { failed, failures } = await uploadPool(
      fileArr,
      async (file) => {
        const compressed = await compressImage(file);
        const { key } = faseId !== undefined
          ? await uploadTaskPhoto(faseId, compressed)
          : await uploadProjectInterventiePhoto(projectId, compressed);
        // Optimistic prepend — show the photo instantly instead of waiting on a re-list.
        // The upload timestamp starts the key's filename (yyyymmdd…), so it buckets under
        // today's date group. The proxy URL mirrors the backend's getProxyUrl (ADR-022).
        setPhotos((prev) =>
          prev.some((p) => p.key === key)
            ? prev
            : [
                { key, url: `/api/photos/serve?key=${encodeURIComponent(key)}`, filename: file.name, lastModified: new Date().toISOString(), size: compressed.size },
                ...prev,
              ],
        );
        setUploadProgress((prev) => (prev ? { ...prev, done: prev.done + 1 } : null));
      },
      2,
    );

    onChanged?.();
    setUploading(false);
    setUploadProgress(null);
    if (failed > 0) setUploadError(describeUploadFailures(failed, failures));

    // Silent reconcile with the server (no loading spinner — the optimistic photos are
    // already visible). The upload invalidated the list cache server-side, so this now
    // returns the fresh list including the new photo instead of dropping it.
    fetchProjectInterventiePhotos(projectId)
      .then((res) => { if (projectIdRef.current === projectId) setPhotos(res); })
      .catch(() => { /* keep the optimistic photos on a transient reconcile error */ });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (uploading) return;
    void handleUpload(e.dataTransfer.files);
  };

  const handleDelete = async (key: string) => {
    if (!confirm('Foto verwijderen?')) return;
    setDeleteError(null);
    try {
      await deleteProjectInterventiePhoto(projectId, key);
      setPhotos((prev) => prev.filter((p) => p.key !== key));
      onChanged?.();
    } catch {
      setDeleteError('Foto verwijderen mislukt');
    }
  };

  // Which date-group is being re-dated (its id) — disables its dropdown while in flight.
  const [redatingGroup, setRedatingGroup] = useState<string | null>(null);
  const [redateError, setRedateError] = useState<string | null>(null);

  const handleRedate = async (group: DisplayGroup, date: string) => {
    setRedatingGroup(group.id);
    setRedateError(null);
    try {
      const { moved } = await redateProjectInterventiePhotos(projectId, group.photos.map((p) => p.key), date);
      // Optimistically re-key locally — the new filename date re-buckets the photos
      // under the target group instantly (merging if that day already exists).
      const map = new Map(moved.map((m) => [m.oldKey, m.newKey]));
      setPhotos((prev) =>
        prev.map((p) => {
          const newKey = map.get(p.key);
          return newKey ? { ...p, key: newKey, url: `/api/photos/serve?key=${encodeURIComponent(newKey)}` } : p;
        }),
      );
      onChanged?.();
      // Silent reconcile — cache was invalidated server-side on the re-key.
      fetchProjectInterventiePhotos(projectId)
        .then((res) => { if (projectIdRef.current === projectId) setPhotos(res); })
        .catch(() => { /* keep the optimistic re-key on a transient reconcile error */ });
    } catch {
      setRedateError('Datum wijzigen mislukt');
      // A partial server failure can leave local keys stale (some moved, some not) —
      // every retry would then 500 on the first already-moved key. Re-sync from the server.
      load();
    } finally {
      setRedatingGroup(null);
    }
  };

  const Wrapper = embedded ? 'div' : 'section';
  if (hideWhenEmpty && !loading && !loadError && groups.length === 0) return null;

  return (
    <Wrapper
      aria-label="Interventies"
      className={embedded ? 'space-y-4' : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-4'}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        {heading}
        {!embedded && !heading && <h3 className="text-base font-extrabold text-td-navy">Interventies</h3>}
        {canUpload && !loading && !loadError && (
          <>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); if (!uploading) setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              disabled={uploading}
              className={`ml-auto inline-flex items-center gap-1.5 rounded-lg border-2 border-dashed px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                isDragOver ? 'border-td-blue bg-td-blue/5 text-td-blue' : 'border-gray-300 text-td-muted hover:border-td-blue hover:text-td-blue'
              }`}
              title="Klik om te uploaden of sleep foto's hierheen"
            >
              {uploading ? (
                <>
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-td-blue/30 border-t-td-blue" />
                  {uploadProgress ? `${uploadProgress.done}/${uploadProgress.total}` : 'Uploaden…'}
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Foto toevoegen
                </>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf,.pdf"
              multiple
              onChange={(e) => {
                // Capture before the reset (handleUpload snapshots the list synchronously).
                if (e.target.files && e.target.files.length) void handleUpload(e.target.files);
                // Reset so re-selecting the SAME file fires onChange again.
                e.target.value = '';
              }}
              className="hidden"
              aria-label="Selecteer interventiefoto's om te uploaden"
            />
          </>
        )}
      </div>

      {uploadError && <p className="text-xs text-red-500" role="alert">{uploadError}</p>}
      {deleteError && <p className="text-xs text-red-500" role="alert">{deleteError}</p>}
      {redateError && <p className="text-xs text-red-500" role="alert">{redateError}</p>}

      {loading ? (
        <p className="text-sm text-td-muted">Interventiefoto's laden…</p>
      ) : loadError ? (
        <div className="flex items-center gap-2 text-sm text-td-magenta">
          <span>{loadError}</span>
          <button type="button" onClick={() => load()} className="font-semibold text-td-blue hover:underline">
            Opnieuw proberen
          </button>
        </div>
      ) : groups.length === 0 ? (
        <p className="text-sm text-td-muted">Nog geen interventiefoto's.</p>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => {
            // Re-date only real date-groups (not "Overige"), onto a planning day other
            // than the group's own. g.id is the group's yyyymmdd; options carry yyyy-mm-dd.
            const dateOptions = g.id === 'overige'
              ? []
              : planningDates.filter((d) => d.date.replace(/-/g, '') !== g.id);
            return (
            <div key={g.id}>
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                <p className="text-xs font-bold text-td-navy">
                  {g.label} <span className="font-semibold text-td-muted">· {fotoCount(g.photos.length)}</span>
                </p>
                {canRedate && dateOptions.length > 0 && (
                  <label className="flex items-center gap-1 text-[11px] text-td-muted">
                    <span>wijzig datum</span>
                    <select
                      aria-label={`Wijzig datum van groep ${g.label}`}
                      value=""
                      disabled={redatingGroup === g.id}
                      onChange={(e) => { const v = e.target.value; if (v) void handleRedate(g, v); e.target.value = ''; }}
                      className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-[11px] text-td-navy focus:border-td-blue focus:outline-none disabled:opacity-50"
                    >
                      <option value="">{redatingGroup === g.id ? 'Bezig…' : 'kies dag…'}</option>
                      {dateOptions.map((d) => (
                        <option key={d.date} value={d.date}>{fmtDay(d.date)} · {d.label}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {g.photos.map((p) => (
                  <div key={p.key} className="group relative shrink-0">
                    <PhotoGridThumb
                      photo={p}
                      tileSizeClass="h-20 w-20"
                      imgClassName="h-20 w-20 rounded-lg border border-gray-200 object-cover transition-colors hover:border-td-blue"
                      onOpen={() => {
                        // The lightbox is image-only; index into the filtered list.
                        const images = g.photos.filter((x) => !isPdfPhoto(x));
                        setLightbox({ photos: images, index: images.findIndex((x) => x.key === p.key) });
                      }}
                    />
                    {canDelete && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); void handleDelete(p.key); }}
                        className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-white/90 opacity-0 shadow-sm transition-opacity hover:bg-red-50 group-hover:opacity-100"
                        aria-label={`Verwijder ${p.filename}`}
                        title="Foto verwijderen"
                      >
                        <svg className="h-3 w-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {lightbox && lightbox.photos.length > 0 && (
        <PhotoLightbox
          photos={lightbox.photos}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </Wrapper>
  );
}
